import _ from 'lodash'
import { flow, cond, lte, constant, stubTrue, get } from 'lodash/fp'
import D from 'decimal.js'
import { d } from 'core/service/lib/decimal'
import constants from 'core/data-model/constants/defaults'

export const accountValueBeforeDeductionIwealthy = (netAllocationPremium, accountValuesAfterBonus) => {
  const previousAccountValueAfterBonus = _.last(accountValuesAfterBonus) || d(0)
  if (previousAccountValueAfterBonus.lt(0)) return d(0)

  return netAllocationPremium.plus(previousAccountValueAfterBonus)
}

export const accountValueBeforeDeductionIinvest = (
  netAllocationPremium,
  accountValuesAfterBonus,
  netLumpsum,
  policyPeriod
) => {
  if (policyPeriod.year === 1 && policyPeriod.month === 1) return netAllocationPremium

  const previousAccountValueAfterBonus = _.last(accountValuesAfterBonus) || d(0)
  if (previousAccountValueAfterBonus.lt(0)) return d(0)

  return previousAccountValueAfterBonus.plus(netLumpsum)
}

export const accountValueAfterDeduction = (accountValueBeforeDeduction, chargeOfInsurance, adminCharge) =>
  accountValueBeforeDeduction.minus(chargeOfInsurance).minus(adminCharge)

export const accountValueBeforeBonus = (accountValueAfterDeduction, fundReturn) =>
  accountValueAfterDeduction.plus(fundReturn)

export const accountValueAfterBonus = (accountValueBeforeBonus, loyaltyBonus) =>
  accountValueBeforeBonus.plus(loyaltyBonus)

export const loyaltyBonus = (
  { month, year },
  loyaltyBonusRates,
  currentAccountValueBeforeBonus,
  accountValueBeforeDeduction,
  accountValuesBeforeBonus
) => {
  if (month !== 12 || accountValueBeforeDeduction.lte(0)) return d(0)

  const loyaltyBonusRateForYear = loyaltyBonusRates.find(
    (r) => r.policyPeriodStart <= year && year <= r.policyPeriodEnd
  )
  if (!loyaltyBonusRateForYear || loyaltyBonusRateForYear.rate === 0.0) return d(0)

  const averageAccountValueBeforeBonus = _(accountValuesBeforeBonus)
    .filter({ year })
    .reduce((acc, accountValue) => acc.plus(d(accountValue.value)), currentAccountValueBeforeBonus)
    .div(d(constants.MONTHS_IN_YEAR))

  return averageAccountValueBeforeBonus.times(loyaltyBonusRateForYear.rate)
}

export const fundReturn = (expectedReturn, accountValueAfterDeduction) =>
  expectedReturn
    .plus(d(1))
    .pow(d(1).div(d(constants.MONTHS_IN_YEAR)))
    .minus(d(1))
    .times(accountValueAfterDeduction)

export const adminChargeRPP = (accountValueBeforeDeductionRPP, amcRate) => accountValueBeforeDeductionRPP.times(amcRate)
export const adminChargeIwealthy = (accountValueBeforeDeduction, amcRate) =>
  accountValueBeforeDeduction.times(amcRate).div(d(constants.MONTHS_IN_YEAR))

export const adminChargeIinvest = (accountValueBeforeDeduction, amcRate, chargeOfInsuranceValue) => {
  const accountValueAfterDeduction = accountValueBeforeDeduction - chargeOfInsuranceValue
  return amcRate.div(d(constants.MONTHS_IN_YEAR)).times(accountValueAfterDeduction)
}

export const chargeOfInsurance = (coiRates, age, gender, sumAtRisk) => {
  const coi = _.find(coiRates[gender], { age })
  if (!coi) {
    throw new Error(`COI Rate missing for gender: ${gender}, age: ${age}`)
  }
  return sumAtRisk
    .times(d(coi.rate))
    .div(d(constants.MONTHS_IN_YEAR))
    .div(d(1000))
}

export const sumAtRiskIwealthy = (sumAssured, accountValueBeforeDeduction, deathBenefitRate) => {
  return D.max(sumAssured, accountValueBeforeDeduction)
    .times(deathBenefitRate)
    .minus(accountValueBeforeDeduction)
}

// TODO: use deathBenefit from product config
export const sumAtRiskIinvest = (sumAssured, accountValueBeforeDeduction) => {
  const hundredTenPercentSumAssuredMinusAccountValue = sumAssured.times(1.1).minus(accountValueBeforeDeduction)
  const tenPercentAccountValue = accountValueBeforeDeduction.times(0.1)
  return D.max(hundredTenPercentSumAssuredMinusAccountValue, tenPercentAccountValue)
}

export const deathBenefit = (sumAssured, accountValueAfterBonus, deathBenefitRate) =>
  D.max(sumAssured, accountValueAfterBonus).times(deathBenefitRate)

export const surrenderCashIWealthy = (
  feeStructure,
  policyYear,
  accountValueAfterBonus,
  currentPremium,
  pastPremium
) => {
  const surrenderChargeForYear = _.find(feeStructure.surrenderCharge, { policyYear }) || { charge: 0 }
  const surrenderCharge = surrenderChargeForYear.charge
  if (surrenderCharge === 0) return accountValueAfterBonus
  const accumulatedNetPremium = _.reduce(
    pastPremium,
    (acc, s) => acc.plus(s.totalPremium).minus(s.premiumCharge),
    currentPremium.totalPremium.minus(currentPremium.premiumCharge)
  )
  const accumulatedNetRegularPremium = _.reduce(
    pastPremium,
    (acc, s) => acc.plus(s.regularPremium).minus(s.regularPremiumCharge),
    currentPremium.regularPremium.minus(currentPremium.regularPremiumCharge)
  )
  const deduction = accountValueAfterBonus
    .times(accumulatedNetRegularPremium)
    .div(accumulatedNetPremium)
    .times(surrenderCharge)
  return accountValueAfterBonus.minus(deduction)
}

export const surrenderCashIInvest = (feeStructure, policyYear, accountValueAfterBonus) => {
  const surrenderChargeForYear = _.find(feeStructure.surrenderCharge, { policyYear }) || { charge: 0 }
  const surrenderCharge = surrenderChargeForYear.charge
  if (surrenderCharge === 0) return accountValueAfterBonus
  const deduction = accountValueAfterBonus.times(surrenderCharge)
  return accountValueAfterBonus.minus(deduction)
}

const isSetAccountValueBenefitDeductRPPToZero = (d) =>
  (d.year > 5 || d.previousRedemptionValueAtMonthEndPlusBonusRPP.toNumber() > 0) &&
  d.previousAccountValuePlusBonusAtMonthEnd.toNumber() <= 0

export const accountValueBenefitDeductRPP = (props) => {
  return flow(
    cond([
      [flow(get('age'), lte(85)), constant(0)],
      [isSetAccountValueBenefitDeductRPPToZero, constant(0)],
      [
        stubTrue,
        constant(
          d(props.previousRedemptionValueAtMonthEndPlusBonusRPP)
            .plus(props.netAllocationRPPPremium)
            .toNumber()
        ),
      ],
    ])
  )(props)
}

const isSetAccountValueBenefitDeductLSTUToZero = (d) => d.previousAccountValuePlusBonusAtMonthEnd.toNumber() <= 0

const isFirstYearAndFirstMonth = ({ year, month }) => year === 1 && month === 1

export const accountValueBenefitDeductLSTU = (props) => {
  return flow(
    cond([
      [isFirstYearAndFirstMonth, constant(props.netAllocationLSTUPremium.toNumber())],
      [flow(get('age'), lte(85)), constant(0)],
      [isSetAccountValueBenefitDeductLSTUToZero, constant(0)],
      [
        stubTrue,
        constant(
          d(props.previousRedemptionValueAtMonthEndPlusBonusLSTU)
            .plus(props.netAllocationLSTUPremium)
            .toNumber()
        ),
      ],
    ])
  )(props)
}
